import { Injectable, computed, signal } from '@angular/core';
import { CartItem } from '../domain/models';

/**
 * Per-merchant cart state, persisted to `localStorage` so a customer
 * who navigates back from Squarespace still sees their line items.
 * Signal-backed — consumers `toSignal()` off this or bind directly.
 *
 * Storage schema (localStorage key `mdp.carts.v1`):
 *   { [merchantSlug]: CartItem[] }
 */
@Injectable({ providedIn: 'root' })
export class CartService {
  private static readonly STORAGE_KEY = 'mdp.carts.v1';

  private readonly _carts = signal<Record<string, CartItem[]>>(loadFromStorage());

  /** Reactive cart lines for a specific merchant. */
  cartFor(merchantSlug: string) {
    return computed(() => this._carts()[merchantSlug] ?? []);
  }

  /** Total quantity across all lines (for the header cart badge). */
  totalQuantityFor(merchantSlug: string) {
    return computed(() =>
      this.cartFor(merchantSlug)().reduce((sum, it) => sum + it.quantity, 0),
    );
  }

  /**
   * Add one unit of [sku]. If [sku] is already in the cart, increments
   * the quantity instead of appending a duplicate line.
   */
  addToCart(merchantSlug: string, sku: string, delta = 1): void {
    this.mutate(merchantSlug, (items) => {
      const existing = items.findIndex((it) => it.sku === sku);
      if (existing >= 0) {
        const next = [...items];
        next[existing] = { sku, quantity: Math.max(1, items[existing].quantity + delta) };
        return next;
      }
      if (delta <= 0) return items;
      return [...items, { sku, quantity: delta }];
    });
  }

  /** Set a specific quantity. Quantities ≤ 0 remove the line. */
  setQuantity(merchantSlug: string, sku: string, quantity: number): void {
    this.mutate(merchantSlug, (items) => {
      if (quantity <= 0) return items.filter((it) => it.sku !== sku);
      const existing = items.findIndex((it) => it.sku === sku);
      if (existing < 0) return [...items, { sku, quantity }];
      const next = [...items];
      next[existing] = { sku, quantity };
      return next;
    });
  }

  remove(merchantSlug: string, sku: string): void {
    this.mutate(merchantSlug, (items) => items.filter((it) => it.sku !== sku));
  }

  clear(merchantSlug: string): void {
    this.mutate(merchantSlug, () => []);
  }

  private mutate(merchantSlug: string, next: (items: CartItem[]) => CartItem[]): void {
    this._carts.update((current) => {
      const prev = current[merchantSlug] ?? [];
      const nextItems = next(prev);
      const out = { ...current, [merchantSlug]: nextItems };
      saveToStorage(out);
      return out;
    });
  }
}

function loadFromStorage(): Record<string, CartItem[]> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(CartService['STORAGE_KEY']);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveToStorage(carts: Record<string, CartItem[]>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(CartService['STORAGE_KEY'], JSON.stringify(carts));
  } catch {
    // Storage full / private mode / etc — fail silently; cart is ephemeral.
  }
}
